import { useContext, useEffect } from "react";
import { useState } from "react";

import SimpleModal from "../../../../../../shared/components/simple-modal/simple-modal";
import Spinner from "../../../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../../../shared/context/auth-context";
import useHttp from "../../../../../../shared/hooks/use-http";
import { getAllData, postSingle } from "../../../../../../shared/utils/api";
import { createUUID, validateLoadingStatusNew } from "../../../../../../shared/utils/utils";
import { noop } from "../../../../../utils";
import { ITableRowData } from "../../../../data-table/data-table";
import ReadonlyWidgetComponent from "../../../readonly-widget-component/readonly-widget-component";
import { IWidgetLayout } from "../../../widget-component/widget-component";

interface IReferenceWidgetModalProps {
  showModal: boolean;
  entityId: string;
  selectedEntityId: string | undefined;
  label: string;
  onModalSubmit: (data: ITableRowData) => void;
  onModalClosed: () => void;
  submitRecordStatus?: string | null;
}

const ReferenceWidgetModal = ({showModal, entityId, selectedEntityId, label, onModalSubmit, onModalClosed, submitRecordStatus}: IReferenceWidgetModalProps) => {
  const authCtx = useContext(AuthContext);
  const [entityLayout, setEntityLayout] = useState<IWidgetLayout | undefined>();
  const [entityData, setEntityData] = useState<ITableRowData>({id: ""});

  const {
    sendRequest: getEntityCapture,
    status: getEntityCaptureStatus,
    data: entityCaptureData,
    error: getEntityCaptureError
  } = useHttp(getAllData, true, authCtx.token, "capture", "manager");

  const { sendRequest: getEntity, status: getEntityStatus, data: getEntityResult, error: getEntityError } = useHttp(postSingle, false, authCtx.token, "datamodelTable", "platform");
  useEffect(() => {
    getEntityCapture(`datamodel_capture/${entityId}/entity`, undefined, undefined, "none", "Entity cannot be used because it does not have a capture screen.")
  }, []);

  useEffect(() => {
    setEntityData({id: ""});
    if (selectedEntityId) {
      getEntity(`datamodel/table/${entityId}/entity`, {
        ids: [selectedEntityId],
        columns: []
      }, undefined, "none");
    }
  }, [entityId, selectedEntityId]);

  useEffect(() => {
    if (getEntityResult && getEntityStatus === "completed" && !getEntityError) {
      setEntityData({...getEntityResult.rows[0]});
    }
  }, [getEntityResult, getEntityStatus, getEntityError]);

  useEffect(() => {
    if (entityCaptureData && getEntityCaptureStatus === "completed" && !getEntityCaptureError) {
      setEntityLayout(entityCaptureData.layout);
    }
  }, [entityCaptureData, getEntityCaptureStatus, getEntityCaptureError, selectedEntityId]);

  const handleOnDataChanged = (newValue: string, key?: string) => {
    if (!key) {
      return;
    }
    setEntityData({...entityData, [key]: newValue});
  };

  const handleModalSubmit = () => {
    const data = {...entityData};
    if (data.id === "") {
      data.id = createUUID();
    }
    onModalSubmit(data);
  };

  const handleModalClosed = () => {
    setEntityData({id: ""});
    onModalClosed();
  };

  const renderModalBody = () => {
    return <div>
      {validateLoadingStatusNew([getEntityCaptureStatus, getEntityStatus, submitRecordStatus]) && <Spinner />}
      {entityLayout && <ReadonlyWidgetComponent
        layout={entityLayout}
        data={entityData}
        onDataChanged={handleOnDataChanged}
      />}
    </div>;
  };

  return <div className="referenceWidgetModal">
    {validateLoadingStatusNew([getEntityCaptureStatus, getEntityStatus]) && <Spinner />}
    <SimpleModal
      mode={selectedEntityId ? `Edit ${label}` : `Add ${label}`}
      onModalClickHandler={noop}
      modalBody={renderModalBody()}
      actionButtonDisabled={validateLoadingStatusNew([getEntityCaptureStatus, getEntityStatus, submitRecordStatus])}
      buttonText={selectedEntityId ? "Save" : "Add"}
      handleModalClosed={handleModalClosed}
      handleSubmit={handleModalSubmit}
      show={showModal}
    />
  </div>;
};

export default ReferenceWidgetModal;